import { Component, OnInit, OnDestroy, Injectable, Inject, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActionCommandService, ActionCommand } from '../../../../services/action-command.service';
import { MenuStateService } from '../../../../services/menu-state.service';
import { CustomerService } from '../../../../services/customer.service';
import { SaleService } from '../../../../services/sale.service';
import { ComandaService } from '../../../../services/comanda.service';
import { PrintService } from '../../../../services/print.service';
import { Customer } from '../../../../models/customer.model';
import { CreateSaleFromPdvDto } from '../../create-sale-from-pdv-dto';
import { Sale } from '../../../../models/sale.model';
import { InputNumberModule } from 'primeng/inputnumber';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

export interface CreditoInstallment {
  numero: number;
  vencimento: string;
  valor: number;
}

export interface CreditoData {
  cliente: Customer | null;
  cpfCnpj: string;
  telefone: string;
  receita: string;
  valorTotal: number;
  numeroParcelas: number;
  primeiroVencimento: string;
  intervalo: number;
  parcelas: CreditoInstallment[];
  valorEntrada: number;
}

@Injectable()
@Component({
  selector: 'app-credito',
  imports: [CommonModule, FormsModule, InputNumberModule, CalendarModule, InputTextModule, ButtonModule, DialogModule, ToastModule, ConfirmDialogModule],
  templateUrl: './credito.component.html',
  styleUrl: './credito.component.scss'
})
export class CreditoComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, ActionCommand {
  
  @Input() valorTotalVenda: number = 0;
  @Input() clienteSelecionado: Customer | null = null;
  @Input() saleData: CreateSaleFromPdvDto | null = null;
  /** Comanda (mesa) a fechar após criar a venda, quando aplicável. */
  @Input() comandaIdToClose: number | null = null;
  @Output() clienteCadastrado = new EventEmitter<Customer>();
  @Output() clienteRemovido = new EventEmitter<void>();
  @Output() clearPdvRequested = new EventEmitter<void>();
  
  @ViewChild('numeroParcelasInput') numeroParcelasInput!: ElementRef;

  // Dados do crediário
  creditoData: CreditoData = {
    cliente: null,
    cpfCnpj: '',
    telefone: '',
    receita: '7 - Venda no Crediário',
    valorTotal: 0,
    numeroParcelas: 1,
    primeiroVencimento: '',
    intervalo: 30,
    parcelas: [],
    valorEntrada: 0
  };

  // Modal de cadastro de cliente
  showCadastroCliente: boolean = false;
  novoCliente: Partial<Customer> = {
    fullName: '',
    cpf: '',
    phone: ''
  };

  primeiroVencimentoDate: Date | null = null;

  /** Campo de data um pouco menor em telas com pouca altura */
  readonly calendarInputStyle: Record<string, string> = {
    'font-size': '0.8125rem',
    'line-height': '1.25rem',
    padding: '0.1875rem 0.5rem',
    'min-height': '1.75rem'
  };

  constructor(
    @Inject(ActionCommandService) private actionCommandService: ActionCommandService,
    @Inject(MenuStateService) private menuStateService: MenuStateService,
    @Inject(CustomerService) private customerService: CustomerService,
    @Inject(SaleService) private saleService: SaleService,
    @Inject(ComandaService) private comandaService: ComandaService,
    @Inject(PrintService) private printService: PrintService,
    @Inject(MessageService) private messageService: MessageService,
    @Inject(ConfirmationService) private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.registerCommands();
    this.inicializarDados();
  }

  ngAfterViewInit(): void {
    // Aguardar um pouco para garantir que o componente está totalmente renderizado
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['valorTotalVenda'] && this.valorTotalVenda > 0) {
      this.creditoData.valorTotal = this.valorTotalVenda;
      if (this.creditoData.valorEntrada > this.creditoData.valorTotal) {
        this.creditoData.valorEntrada = this.creditoData.valorTotal;
      }
      this.calcularParcelas();
    }
    if (changes['clienteSelecionado']) {
      this.setClienteSelecionado(this.clienteSelecionado);
    }
    if (changes['saleData'] && this.saleData) {
      // Atualizar valor total se necessário
      if (this.saleData.netTotal > 0 && this.creditoData.valorTotal !== this.saleData.netTotal) {
        this.creditoData.valorTotal = this.saleData.netTotal;
        if (this.creditoData.valorEntrada > this.creditoData.valorTotal) {
          this.creditoData.valorEntrada = this.creditoData.valorTotal;
        }
        this.calcularParcelas();
      }
    }
  }

  private inicializarDados(): void {
    // Inicializar com o valor total da venda
    if (this.valorTotalVenda > 0) {
      this.creditoData.valorTotal = this.valorTotalVenda;
    }
    // Inicializar cliente se fornecido
    if (this.clienteSelecionado) {
      this.setClienteSelecionado(this.clienteSelecionado);
    }
    // Inicializar data do primeiro vencimento (30 dias a partir de hoje)
    const hoje = new Date();
    hoje.setDate(hoje.getDate() + 30);
    this.primeiroVencimentoDate = hoje;
    this.creditoData.primeiroVencimento = hoje.toLocaleDateString('pt-BR');
    this.calcularParcelas();
  }

  ngOnDestroy(): void {
    this.unregisterCommands();
  }

  private registerCommands(): void {
    // Registrar comando F7 para confirmar
    this.actionCommandService.registerCommand({
      getKey: () => 'F7',
      execute: () => this.confirmarCredito(),
      getDescription: () => 'Confirmar Crediário'
    });

    // Registrar comando ESC para voltar
    this.actionCommandService.registerCommand({
      getKey: () => 'ESC',
      execute: () => this.voltar(),
      getDescription: () => 'Voltar'
    });
  }

  private unregisterCommands(): void {
    this.actionCommandService.unregisterCommand('F7');
    this.actionCommandService.unregisterCommand('ESC');
  }

  // Método para receber cliente selecionado do componente pai
  setClienteSelecionado(cliente: Customer | null): void {
    this.creditoData.cliente = cliente;
    if (cliente) {
      this.creditoData.cpfCnpj = cliente.cpf || '';
      this.creditoData.telefone = cliente.phone || '';
    } else {
      this.creditoData.cpfCnpj = '';
      this.creditoData.telefone = '';
    }
  }

  removerCliente(): void {
    this.clienteRemovido.emit();
  }

  // Método para focar no campo de número de parcelas
  focusNumeroParcelas(): void {
    setTimeout(() => {
      // Primeiro tentar pelo ID
      const inputById = document.getElementById('numero-parcelas-input');
      if (inputById && inputById instanceof HTMLInputElement) {
        inputById.focus();
        inputById.select();
        return;
      }
      
      // Tentar acessar o input do p-inputNumber via ViewChild
      if (this.numeroParcelasInput && this.numeroParcelasInput.nativeElement) {
        // O PrimeNG InputNumber renderiza um input dentro de uma div
        const inputElement = this.numeroParcelasInput.nativeElement.querySelector('input');
        if (inputElement && inputElement instanceof HTMLInputElement) {
          inputElement.focus();
          inputElement.select();
          return;
        }
      }
      
      // Fallback: tentar encontrar pela classe do PrimeNG
      setTimeout(() => {
        const inputByClass = document.querySelector('.p-inputnumber-input') as HTMLInputElement;
        if (inputByClass) {
          inputByClass.focus();
          inputByClass.select();
        }
      }, 50);
    }, 150);
  }

  onNumeroParcelasChange(): void {
    if (this.creditoData.numeroParcelas < 1) {
      this.creditoData.numeroParcelas = 1;
    }
    this.calcularParcelas();
  }

  onPrimeiroVencimentoChange(): void {
    if (this.primeiroVencimentoDate) {
      this.creditoData.primeiroVencimento = this.primeiroVencimentoDate.toLocaleDateString('pt-BR');
      this.calcularParcelas();
    }
  }

  onIntervaloChange(): void {
    if (this.creditoData.intervalo < 1) {
      this.creditoData.intervalo = 1;
    }
    this.calcularParcelas();
  }

  onValorEntradaChange(): void {
    const max = this.creditoData.valorTotal;
    if (this.creditoData.valorEntrada < 0) {
      this.creditoData.valorEntrada = 0;
    }
    if (this.creditoData.valorEntrada > max) {
      this.creditoData.valorEntrada = max;
    }
    this.calcularParcelas();
  }

  private static roundMoney(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private calcularParcelas(): void {
    const totalVenda = CreditoComponent.roundMoney(this.creditoData.valorTotal);
    const entrada = CreditoComponent.roundMoney(Math.min(this.creditoData.valorEntrada || 0, totalVenda));
    const financed = CreditoComponent.roundMoney(totalVenda - entrada);
    const n = this.creditoData.numeroParcelas;

    if (financed <= 0 || n <= 0) {
      this.creditoData.parcelas = [];
      return;
    }

    this.creditoData.parcelas = [];
    const baseShare = CreditoComponent.roundMoney(financed / n);
    let allocated = 0;

    let dataVencimento: Date;
    if (this.primeiroVencimentoDate) {
      dataVencimento = new Date(this.primeiroVencimentoDate);
    } else if (this.creditoData.primeiroVencimento) {
      dataVencimento = new Date(this.creditoData.primeiroVencimento.split('/').reverse().join('-'));
    } else {
      dataVencimento = new Date();
      dataVencimento.setDate(dataVencimento.getDate() + 30);
    }

    for (let i = 0; i < n; i++) {
      const vencimento = new Date(dataVencimento);
      vencimento.setDate(vencimento.getDate() + i * this.creditoData.intervalo);
      const isLast = i === n - 1;
      const valorParcela = isLast ? CreditoComponent.roundMoney(financed - allocated) : baseShare;
      allocated = CreditoComponent.roundMoney(allocated + valorParcela);

      this.creditoData.parcelas.push({
        numero: i + 1,
        vencimento: vencimento.toLocaleDateString('pt-BR'),
        valor: valorParcela
      });
    }
  }

  abrirCadastroCliente(): void {
    this.showCadastroCliente = true;
    this.novoCliente = {
      fullName: '',
      cpf: '',
      phone: ''
    };
  }

  // Método público para ser chamado pelo componente pai
  public openCadastroCliente(): void {
    this.abrirCadastroCliente();
  }

  fecharCadastroCliente(): void {
    this.showCadastroCliente = false;
    this.novoCliente = {
      fullName: '',
      cpf: '',
      phone: ''
    };
  }

  salvarNovoCliente(): void {
    if (!this.novoCliente.fullName || !this.novoCliente.fullName.trim()) {
      alert('Por favor, informe o nome do cliente.');
      return;
    }

    const cliente: Customer = {
      id: 0,
      status: 'active',
      active: true,
      fullName: this.novoCliente.fullName || '',
      cpf: this.novoCliente.cpf || '',
      phone: this.novoCliente.phone || '',
      address: '',
      addressComplement: '',
      addressNumber: '',
      birthDay: new Date(),
      cashBack: 0,
      cep: '',
      city: '',
      contact: '',
      creditLimit: 0,
      district: '',
      dueDay: 0,
      email: '',
      homePhone: '',
      installmentPriceTable: false,
      moneyPriceTable: false,
      nickName: '',
      observations: '',
      order: null as any,
      rg: '',
      situation: '',
      state: '',
      usePriceTable: false,
      whatsApp: '',
      wholeSalePriceTable: false
    };

    this.customerService.create(cliente).subscribe({
      next: (clienteCriado) => {
        this.setClienteSelecionado(clienteCriado);
        this.clienteCadastrado.emit(clienteCriado);
        this.fecharCadastroCliente();
        alert('Cliente cadastrado com sucesso!');
      },
      error: (error) => {
        console.error('Erro ao cadastrar cliente:', error);
        alert('Erro ao cadastrar cliente. Por favor, tente novamente.');
      }
    });
  }

  confirmarCredito(): void {
    // Validar se há cliente selecionado
    if (!this.creditoData.cliente) {
      alert('Por favor, selecione um cliente.');
      return;
    }

    // Validar se há dados da venda
    if (!this.saleData) {
      alert('Erro: Dados da venda não disponíveis.');
      return;
    }

    const totalRestante = CreditoComponent.roundMoney(this.creditoData.valorTotal);
    const entrada = CreditoComponent.roundMoney(Math.min(this.creditoData.valorEntrada || 0, totalRestante));
    const financed = CreditoComponent.roundMoney(totalRestante - entrada);
    if (financed <= 0) {
      alert('O valor financiado (total menos entrada) deve ser maior que zero. Ajuste a entrada ou use outra forma de pagamento.');
      return;
    }

    // Validar se há parcelas configuradas
    if (this.creditoData.parcelas.length === 0) {
      alert('Por favor, configure as parcelas do crediário.');
      return;
    }

    // Preparar dados da venda com pagamento em crediário
    const saleToCreate = this.prepareSaleData();

    this.saleService.create(saleToCreate as unknown as Sale).subscribe({
      next: (response) => {
        const saleId = response?.data?.saleId;
        const comandaId = this.comandaIdToClose;
        if (comandaId != null) {
          this.comandaService.fechar(comandaId).subscribe({
            next: () => this.afterSaleCreatedSuccess(saleId),
            error: () => this.afterSaleCreatedSuccess(saleId)
          });
        } else {
          this.afterSaleCreatedSuccess(saleId);
        }
      },
      error: (error) => {
        console.error('Erro ao criar venda:', error);
        const errorMessage = error?.error?.message || 'Não foi possível finalizar a venda. Por favor, tente novamente.';
        
        // Verificar se o serviço está disponível
        if (!this.messageService) {
          console.error('MessageService não está disponível');
          alert('Erro: ' + errorMessage);
        } else {
          // Mostrar mensagem de erro
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Erro!', 
            detail: errorMessage, 
            life: 5000 
          });
        }
      }
    });
  }

  private afterSaleCreatedSuccess(saleId?: number): void {
    if (!this.messageService) {
      alert('Venda finalizada com sucesso!');
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso!',
        detail: 'Venda finalizada com sucesso!',
        life: 3000
      });
    }

    if (!this.confirmationService) {
      const imprimir = confirm('Deseja imprimir cupom da venda e parcelas do crediário?');
      if (imprimir && saleId != null && saleId > 0) {
        this.printService.printSaleReceipt(saleId);
        this.printService.printCrediarioInstallments(saleId);
      }
    } else {
      this.confirmationService.confirm({
        message: 'Deseja imprimir o cupom da venda e as parcelas do crediário?',
        header: 'Impressão',
        icon: 'pi pi-print',
        acceptLabel: 'Sim',
        rejectLabel: 'Não',
        defaultFocus: 'accept',
        accept: () => {
          if (saleId != null && saleId > 0) {
            this.printService.printSaleReceipt(saleId);
            this.printService.printCrediarioInstallments(saleId);
          }
        },
        reject: () => {}
      });

      const tryFocusAccept = (attempt: number = 0) => {
        const acceptButton = document.querySelector('.p-confirm-dialog-accept') as HTMLButtonElement;
        if (acceptButton) {
          acceptButton.focus();
          setTimeout(() => {
            if (document.activeElement !== acceptButton) {
              acceptButton.focus();
            }
          }, 50);
        } else if (attempt < 5) {
          setTimeout(() => {
            tryFocusAccept(attempt + 1);
          }, 100 * (attempt + 1));
        }
      };

      setTimeout(() => {
        tryFocusAccept(0);
      }, 150);
    }

    this.clearPdv();
    this.menuStateService.closeAdminMenu();
  }

  private prepareSaleData(): Record<string, unknown> {
    if (!this.saleData) {
      throw new Error('Dados da venda não disponíveis');
    }

    const totalRestante = CreditoComponent.roundMoney(this.creditoData.valorTotal);
    const entrada = CreditoComponent.roundMoney(Math.min(this.creditoData.valorEntrada || 0, totalRestante));
    const financed = CreditoComponent.roundMoney(totalRestante - entrada);
    const partialTotals = this.getPartialPaymentTotalsFromSale();
    const pdvIdRaw = typeof localStorage !== 'undefined' ? localStorage.getItem('pdvId') : null;
    const shiftParsed = pdvIdRaw ? parseInt(pdvIdRaw, 10) : NaN;
    const shiftFromStorage = Number.isFinite(shiftParsed) ? shiftParsed : null;
    const firstDueIso = this.primeiroVencimentoDate
      ? new Date(
          this.primeiroVencimentoDate.getFullYear(),
          this.primeiroVencimentoDate.getMonth(),
          this.primeiroVencimentoDate.getDate()
        ).toISOString()
      : null;

    return {
      ...this.saleData,
      customerId: this.creditoData.cliente?.id || this.saleData.customerId,
      customerName: this.creditoData.cliente?.fullName || this.saleData.customerName,
      customerDocument: this.creditoData.cliente?.cpf || this.saleData.customerDocument || null,
      shiftId: this.saleData.shiftId ?? shiftFromStorage,
      moneyTotal: CreditoComponent.roundMoney(partialTotals['MONEY'] + entrada),
      pixTotal: partialTotals['PIX'],
      creditCardTotal: partialTotals['CREDIT_CARD'],
      debitCardTotal: partialTotals['DEBIT_CARD'],
      checkTotal: partialTotals['CHECK'],
      creditTotal: CreditoComponent.roundMoney(partialTotals['CREDIT'] + financed),
      bankTransferTotal: partialTotals['BANK_TRANSFER'],
      voucherTotal: partialTotals['VOUCHER'],
      depositTotal: 0,
      cashBackTotal: 0,
      exchange: 0,
      downPayment: entrada,
      installmentsFinancial: this.creditoData.numeroParcelas,
      initialInstallmentDate: firstDueIso,
      installmentIntervalDays: this.creditoData.intervalo,
      typePaymentId: 6
    };
  }

  private clearPdv(): void {
    this.clearPdvRequested.emit();
  }

  private getPartialPaymentTotalsFromSale(): Record<string, number> {
    const totals: Record<string, number> = {
      MONEY: 0,
      PIX: 0,
      CREDIT_CARD: 0,
      DEBIT_CARD: 0,
      CHECK: 0,
      CREDIT: 0,
      BANK_TRANSFER: 0,
      VOUCHER: 0
    };

    totals['MONEY'] = CreditoComponent.roundMoney(this.saleData?.moneyTotal ?? 0);
    totals['PIX'] = CreditoComponent.roundMoney(this.saleData?.pixTotal ?? 0);
    totals['CREDIT_CARD'] = CreditoComponent.roundMoney(this.saleData?.creditCardTotal ?? 0);
    totals['DEBIT_CARD'] = CreditoComponent.roundMoney(this.saleData?.debitCardTotal ?? 0);
    totals['CHECK'] = CreditoComponent.roundMoney(this.saleData?.checkTotal ?? 0);
    totals['CREDIT'] = CreditoComponent.roundMoney(this.saleData?.creditTotal ?? 0);
    totals['BANK_TRANSFER'] = CreditoComponent.roundMoney(this.saleData?.bankTransferTotal ?? 0);
    totals['VOUCHER'] = CreditoComponent.roundMoney(this.saleData?.voucherTotal ?? 0);

    return totals;
  }

  voltar(): void {
    this.menuStateService.goBackToMainMenu();
  }

  // Implementação da interface ActionCommand
  getKey(): string {
    return 'F7';
  }

  execute(): void {
    this.confirmarCredito();
  }

  getDescription(): string {
    return 'Confirmar Crediário';
  }
}
